import React, { useState, useMemo, useRef, useEffect } from 'react';
import { PNLPivotTable } from './PNLPivotTable';

import { useTheme } from '../ThemeContext';
import { formatRupiah } from '../data/financialData';
import type { Area } from '../data/financialData';
import { useData } from '../DataContext';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

// ─── Props ────────────────────────────────────────────────────────────────────

interface BEPPageProps {
  area: Area;
  branch: string;
  startIdx: number;
  endIdx: number;
}

// ─── Result Types ─────────────────────────────────────────────────────────────

export interface BranchBEPResult {
  code: string;
  name: string;
  area: string;
  status: string;
  monthsOperating: number;
  monthRevenue: number;
  monthExpenses: number;
  monthProfit: number;
  bep: number;
  bepStatus: 'Pass' | 'Ongoing';
  gapToPass: number;
  gapToPassPercent: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getBranchStartIdx(code: string, openDate: string, branchesData: any[], plByBranch: any, monthsData?: any[]): number {
  const branch = branchesData.find((b: any) => b.code === code);
  if (!branch || !plByBranch[code]) {
    return 0;
  }

  const pl = plByBranch[code];

  for (let i = 0; i < Math.min(pl.revenue.total.length, pl.cogs.total.length); i++) {
    const hasRevenue = (pl.revenue.total[i] || 0) !== 0;
    const hasCogs = (pl.cogs.total[i] || 0) !== 0;
    const hasOpex = (pl.opex.total[i] || 0) !== 0;

    if (hasRevenue || hasCogs || hasOpex) {
      return i;
    }
  }

  if (monthsData) {
    const openMonth = openDate.substring(0, 7);
    for (let i = 0; i < monthsData.length; i++) {
      if (monthsData[i].month >= openMonth) {
        return i;
      }
    }
  }
  return 0;
}

function getBranchBE(code: string, currentMonthIdx: number, branchesData: any[], plByBranch: any): BranchBEPResult | null {
  const branch = branchesData.find((b: any) => b.code === code);
  if (!branch || !plByBranch[code]) return null;

  if (branch.openDate === 'TBA') return null;

  const pl = plByBranch[code];
  const startIdx = getBranchStartIdx(code, branch.openDate, branchesData, plByBranch);

  if (startIdx > currentMonthIdx) return null;

  let bep = 0;
  for (let i = startIdx; i <= currentMonthIdx; i++) {
    bep += pl.netProfit[i] || 0;
  }

  const monthsOperating = currentMonthIdx - startIdx + 1;
  const monthRevenue = pl.revenue.total[currentMonthIdx] || 0;
  const monthExpenses =
    (pl.cogs.total[currentMonthIdx] || 0) + (pl.opex.total[currentMonthIdx] || 0);
  const monthProfit = pl.netProfit[currentMonthIdx] || 0;
  const bepStatus = bep >= 0 ? 'Pass' : 'Ongoing';

  const gapToPass = bep < 0 ? -bep : 0;
  const gapToPassPercent = monthProfit > 0 ? gapToPass / monthProfit : -1;

  return {
    code,
    name: branch.name,
    area: branch.area,
    status: branch.status,
    monthsOperating,
    monthRevenue,
    monthExpenses,
    monthProfit,
    bep,
    bepStatus,
    gapToPass,
    gapToPassPercent,
  };
}

function getAreaBranchBEPs(
  filterArea: string,
  currentMonthIdx: number,
  branchesData: any[],
  plByBranch: any
): BranchBEPResult[] {
  const filtered = branchesData.filter(
    (b: any) => filterArea === 'ALL' || b.area === filterArea
  );
  return filtered
    .map((b: any) => getBranchBE(b.code, currentMonthIdx, branchesData, plByBranch))
    .filter((r): r is BranchBEPResult => r !== null);
}

// ─── Component ────────────────────────────────────────────────────────────────

const BEPPage: React.FC<BEPPageProps> = ({ area: initialArea, branch: initialBranch, startIdx, endIdx }) => {
  const { MONTHS, AREAS, branches } = useData();
  const [view, setView] = useState<'summary' | 'area' | 'chart'>('summary');
  const [filterArea, setFilterArea] = useState<Area>(initialArea);
  const [selectedBranches, setSelectedBranches] = useState<string[]>(
    initialBranch && initialBranch !== 'ALL' ? [initialBranch] : []
  );

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFilterArea(initialArea);
    setSelectedBranches(initialBranch && initialBranch !== 'ALL' ? [initialBranch] : []);
  }, [initialArea, initialBranch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const branchesInArea = useMemo(() => {
    return branches.filter((b: any) => filterArea === 'ALL' || b.area === filterArea);
  }, [filterArea, branches]);

  return (
    <div className="data-table-card animate-in">
      <div className="data-table-header">
        <div>
          <div className="data-table-title">📈 BEP Status</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Break-Even Point analysis per cabang — s/d {MONTHS[endIdx]?.label}
          </div>
        </div>
        {/* Tab Switcher */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {(['summary', 'area', 'chart'] as const).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: '6px 14px',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '6px',
                border: '1px solid var(--border-light)',
                cursor: 'pointer',
                backgroundColor: view === v ? '#10b981' : 'var(--bg-secondary)',
                color: view === v ? '#fff' : 'var(--text-muted)',
                transition: 'all 0.15s ease',
              }}
            >
              {v === 'summary' ? 'Per Cabang' : v === 'area' ? 'Per Area' : 'Chart BEP'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>Area:</label>
          <select
            value={filterArea}
            onChange={(e) => {
              setFilterArea(e.target.value as Area);
              setSelectedBranches([]);
            }}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '4px',
              border: '1px solid var(--border-light)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            {AREAS.map(a => (
              <option key={a} value={a}>{a === 'ALL' ? 'Semua Area' : a}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
          <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>Cabang:</label>
          
          <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', width: '250px' }}>
            {/* Trigger Button */}
            <div
              onClick={() => setIsOpen(!isOpen)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '6px',
                border: '1px solid var(--border-light)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '32px',
                boxSizing: 'border-box',
                userSelect: 'none',
                transition: 'all 0.2s ease',
                boxShadow: isOpen ? '0 0 0 2px rgba(16, 185, 129, 0.2)' : 'none',
                borderColor: isOpen ? '#10b981' : 'var(--border-light)',
              }}
            >
              <span style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                marginRight: '8px',
                maxWidth: '200px'
              }}>
                {selectedBranches.length === 0 
                  ? 'Semua Cabang' 
                  : selectedBranches.length === 1 
                    ? `${selectedBranches[0]} — ${branches.find(b => b.code === selectedBranches[0])?.name || ''}`
                    : `${selectedBranches.length} Cabang Terpilih`
                }
              </span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{
                  transition: 'transform 0.2s ease',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: 'var(--text-muted)'
                }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>

            {/* Floating Dropdown Panel */}
            {isOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  width: '300px',
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.15)',
                  marginTop: '6px',
                  zIndex: 1000,
                  padding: '8px',
                  boxSizing: 'border-box',
                }}
              >
                {/* Search input */}
                <div style={{ position: 'relative', marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Cari kode atau nama cabang..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px 6px 26px',
                      fontSize: '11px',
                      borderRadius: '4px',
                      border: '1px solid var(--border-light)',
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      position: 'absolute',
                      left: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  {searchTerm && (
                    <span
                      onClick={() => setSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '10px',
                        color: 'var(--text-muted)',
                        fontWeight: 'bold',
                      }}
                    >
                      ×
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    paddingBottom: '6px',
                    borderBottom: '1px solid var(--border-light)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      const allCodes = branchesInArea.map(b => b.code);
                      setSelectedBranches(allCodes);
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#10b981',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '2px 4px',
                    }}
                  >
                    Pilih Semua
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedBranches([])}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: 'var(--text-muted)',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '2px 4px',
                    }}
                  >
                    Bersihkan
                  </button>
                </div>

                {/* Scrollable list */}
                <div
                  style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  {(() => {
                    const filtered = branchesInArea.filter(
                      b =>
                        b.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        b.name.toLowerCase().includes(searchTerm.toLowerCase())
                    );

                    if (filtered.length === 0) {
                      return (
                        <div style={{ padding: '12px', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center' }}>
                          Tidak ada cabang ditemukan.
                        </div>
                      );
                    }

                    return filtered.map((b) => {
                      const isChecked = selectedBranches.includes(b.code);
                      return (
                        <div
                          key={b.code}
                          onClick={() => {
                            if (isChecked) {
                              setSelectedBranches(selectedBranches.filter(code => code !== b.code));
                            } else {
                              setSelectedBranches([...selectedBranches, b.code]);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            color: 'var(--text-primary)',
                            backgroundColor: isChecked ? 'rgba(16, 185, 129, 0.08)' : 'transparent',
                            transition: 'background-color 0.15s ease',
                            userSelect: 'none',
                          }}
                          onMouseEnter={(e) => {
                            if (!isChecked) e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                          }}
                          onMouseLeave={(e) => {
                            if (!isChecked) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}} // handled by parent div click
                            style={{
                              cursor: 'pointer',
                              accentColor: '#10b981',
                              width: '13px',
                              height: '13px',
                            }}
                          />
                          <span style={{ fontWeight: isChecked ? '600' : 'normal' }}>
                            {b.code} — {b.name}
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {view === 'summary' && <SummaryView area={filterArea} selectedBranches={selectedBranches} endIdx={endIdx} />}
      {view === 'area' && <AreaView area={filterArea} selectedBranches={selectedBranches} endIdx={endIdx} />}
      {view === 'chart' && <ChartView area={filterArea} selectedBranches={selectedBranches} startIdx={startIdx} endIdx={endIdx} />}
    </div>
  );
};

// ─── Sub-views ────────────────────────────────────────────────────────────────

const SummaryView: React.FC<{ area: Area; selectedBranches: string[]; endIdx: number }> = ({ area, selectedBranches, endIdx }) => {
  const { branches: branchesData, profitLossByBranch: plByBranch } = useData();

  const results = useMemo(() => {
    const data = getAreaBranchBEPs(area, endIdx, branchesData, plByBranch);
    const filtered = selectedBranches.length === 0 ? data : data.filter(r => selectedBranches.includes(r.code));
    return filtered.sort((a, b) => b.monthsOperating - a.monthsOperating);
  }, [area, selectedBranches, endIdx, branchesData, plByBranch]);

  const passCount = results.filter(r => r.bepStatus === 'Pass').length;
  const ongoingCount = results.filter(r => r.bepStatus === 'Ongoing').length;

  const totals = useMemo(() => {
    return {
      monthRevenue: results.reduce((sum, r) => sum + r.monthRevenue, 0),
      monthExpenses: results.reduce((sum, r) => sum + r.monthExpenses, 0),
      monthProfit: results.reduce((sum, r) => sum + r.monthProfit, 0),
      bep: results.reduce((sum, r) => sum + r.bep, 0),
      gapToPass: results.reduce((sum, r) => sum + r.gapToPass, 0),
    };
  }, [results]);

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: '#d1fae5', color: '#065f46' }}>
            ✓ Pass: {passCount}
          </div>
          <div style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#92400e' }}>
            ⏳ Ongoing: {ongoingCount}
          </div>
        </div>
      </div>

      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
            <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Kode</th>
            <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Nama Cabang</th>
            <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Kota</th>
            <th style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Waktu Operasional</th>
            <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Pendapatan</th>
            <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Pengeluaran</th>
            <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Keuntungan/Kerugian</th>
            <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>BEP Kumulatif</th>
            <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Gap to Pass</th>
            <th style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result, idx) => (
            <tr
              key={result.code}
              style={{
                backgroundColor: idx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                borderBottom: '1px solid var(--border-color)',
              }}
            >
              <td style={{ padding: '10px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>{result.code}</td>
              <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }}>{result.name}</td>
              <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>{result.area}</td>
              <td style={{ textAlign: 'center', padding: '10px 8px', color: 'var(--text-secondary)' }}>{result.monthsOperating} bulan</td>
              <td style={{ textAlign: 'right', padding: '10px 8px', color: 'var(--text-secondary)' }}>{formatRupiah(result.monthRevenue)}</td>
              <td style={{ textAlign: 'right', padding: '10px 8px', color: 'var(--text-secondary)' }}>{formatRupiah(result.monthExpenses)}</td>
              <td style={{
                textAlign: 'right',
                padding: '10px 8px',
                color: result.monthProfit >= 0 ? '#10b981' : '#ef4444',
                fontWeight: '600',
              }}>
                {formatRupiah(result.monthProfit)}
              </td>
              <td style={{
                textAlign: 'right',
                padding: '10px 8px',
                color: result.bep >= 0 ? '#10b981' : '#ef4444',
                fontWeight: '600',
              }}>
                {formatRupiah(result.bep)}
              </td>
              <td style={{
                textAlign: 'right',
                padding: '10px 8px',
                color: result.gapToPass > 0 ? '#f59e0b' : 'var(--text-secondary)',
                fontWeight: '600',
                fontSize: '12px',
              }}>
                <div style={{ color: result.gapToPass > 0 ? '#f59e0b' : 'var(--text-secondary)' }}>
                  {result.gapToPass > 0 ? formatRupiah(result.gapToPass) : '—'}
                </div>
                {result.gapToPass > 0 && (
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {result.gapToPassPercent > 0
                      ? `(${result.gapToPassPercent.toFixed(1)} bulan)`
                      : '(N/A)'}
                  </div>
                )}
              </td>
              <td style={{ textAlign: 'center', padding: '10px 8px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: result.bepStatus === 'Pass' ? '#d1fae5' : '#fef3c7',
                    color: result.bepStatus === 'Pass' ? '#065f46' : '#92400e',
                  }}
                >
                  {result.bepStatus === 'Pass' ? '✓ Pass' : '⏳ Ongoing'}
                </span>
              </td>
            </tr>
          ))}
          <tr style={{ backgroundColor: 'var(--bg-secondary)', borderTop: '2px solid var(--border-color)', fontWeight: '600' }}>
            <td colSpan={4} style={{ padding: '10px 8px', color: 'var(--text-primary)' }}>TOTAL</td>
            <td style={{ textAlign: 'right', padding: '10px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>{formatRupiah(totals.monthRevenue)}</td>
            <td style={{ textAlign: 'right', padding: '10px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>{formatRupiah(totals.monthExpenses)}</td>
            <td style={{
              textAlign: 'right',
              padding: '10px 8px',
              color: totals.monthProfit >= 0 ? '#10b981' : '#ef4444',
              fontWeight: '600',
            }}>
              {formatRupiah(totals.monthProfit)}
            </td>
            <td style={{
              textAlign: 'right',
              padding: '10px 8px',
              color: totals.bep >= 0 ? '#10b981' : '#ef4444',
              fontWeight: '600',
            }}>
              {formatRupiah(totals.bep)}
            </td>
            <td style={{
              textAlign: 'right',
              padding: '10px 8px',
              color: totals.gapToPass > 0 ? '#f59e0b' : 'var(--text-secondary)',
              fontWeight: '600',
            }}>
              {totals.gapToPass > 0 ? formatRupiah(totals.gapToPass) : '—'}
            </td>
            <td style={{ textAlign: 'center', padding: '10px 8px' }}>—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const AreaView: React.FC<{ area: Area; selectedBranches: string[]; endIdx: number }> = ({ selectedBranches, endIdx }) => {
  const { MONTHS, branches: branchesData, profitLossByBranch: plByBranch } = useData();
  const priorIdx = endIdx - 1;

  const areaMetrics = useMemo(() => {
    const areas: Area[] = ['Depok', 'Bogor', 'Bekasi'];
    const metrics = areas.map(a => {
      let currentResults = getAreaBranchBEPs(a, endIdx, branchesData, plByBranch);
      let priorResults = priorIdx >= 0 ? getAreaBranchBEPs(a, priorIdx, branchesData, plByBranch) : [];

      if (selectedBranches.length > 0) {
        currentResults = currentResults.filter(r => selectedBranches.includes(r.code));
        priorResults = priorResults.filter(r => selectedBranches.includes(r.code));
      }

      const currentBEP = currentResults.reduce((sum, r) => sum + r.bep, 0);
      const currentNetProfit = currentResults.reduce((sum, r) => sum + r.monthProfit, 0);
      const priorNetProfit = priorResults.reduce((sum, r) => sum + r.monthProfit, 0);
      const branchCount = currentResults.length;

      return {
        area: a,
        branchCount,
        priorNetProfit,
        currentNetProfit,
        currentBEP,
      };
    });

    const totals = {
      area: 'TOTAL',
      branchCount: metrics.reduce((sum, m) => sum + m.branchCount, 0),
      priorNetProfit: metrics.reduce((sum, m) => sum + m.priorNetProfit, 0),
      currentNetProfit: metrics.reduce((sum, m) => sum + m.currentNetProfit, 0),
      currentBEP: metrics.reduce((sum, m) => sum + m.currentBEP, 0),
    };

    return [...metrics, totals];
  }, [endIdx, priorIdx, branchesData, plByBranch, selectedBranches]);

  const priorLabel = priorIdx >= 0 ? MONTHS[priorIdx]?.label : 'N/A';
  const currentLabel = MONTHS[endIdx]?.label || '';

  return (
    <div style={{ padding: '20px' }}>
      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
            <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Kota</th>
            <th style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>Jumlah Cabang</th>
            <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>PNL {priorLabel}</th>
            <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>PNL {currentLabel}</th>
            <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--text-primary)', fontWeight: '600' }}>BEP Kumulatif</th>
          </tr>
        </thead>
        <tbody>
          {areaMetrics.map((metric, idx) => {
            const isTotal = metric.area === 'TOTAL';
            return (
              <tr
                key={metric.area}
                style={{
                  backgroundColor: isTotal ? 'var(--bg-secondary)' : (idx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-secondary)'),
                  borderBottom: isTotal ? '2px solid var(--border-color)' : '1px solid var(--border-color)',
                  borderTop: isTotal ? '2px solid var(--border-color)' : undefined,
                  fontWeight: isTotal ? '600' : undefined,
                }}
              >
                <td style={{ padding: '10px 8px', color: 'var(--text-primary)', fontWeight: isTotal ? '600' : undefined }}>{metric.area}</td>
                <td style={{ textAlign: 'center', padding: '10px 8px', color: 'var(--text-secondary)' }}>{metric.branchCount}</td>
                <td style={{
                  textAlign: 'right',
                  padding: '10px 8px',
                  color: metric.priorNetProfit >= 0 ? '#10b981' : '#ef4444',
                  fontWeight: isTotal ? '600' : undefined,
                }}>
                  {formatRupiah(metric.priorNetProfit)}
                </td>
                <td style={{
                  textAlign: 'right',
                  padding: '10px 8px',
                  color: metric.currentNetProfit >= 0 ? '#10b981' : '#ef4444',
                  fontWeight: isTotal ? '600' : undefined,
                }}>
                  {formatRupiah(metric.currentNetProfit)}
                </td>
                <td style={{
                  textAlign: 'right',
                  padding: '10px 8px',
                  color: metric.currentBEP >= 0 ? '#10b981' : '#ef4444',
                  fontWeight: isTotal ? '600' : undefined,
                }}>
                  {formatRupiah(metric.currentBEP)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ChartView: React.FC<{ area: Area; selectedBranches: string[]; startIdx: number; endIdx: number }> = ({ area, selectedBranches, startIdx, endIdx }) => {
  const { MONTHS, branches: branchesData, profitLossByBranch: plByBranch } = useData();
  const { chart: chartTheme } = useTheme();
  
  const chartRef = useRef<any>(null);
  const [copied, setCopied] = useState(false);

  const effectiveStartIdx = useMemo(() => {
    if (startIdx === 0 && selectedBranches.length === 1) {
      const singleBranch = selectedBranches[0];
      const branchItem = branchesData.find((b: any) => b.code === singleBranch);
      if (branchItem && branchItem.openDate !== 'TBA') {
        return getBranchStartIdx(singleBranch, branchItem.openDate, branchesData, plByBranch);
      }
    }
    return startIdx;
  }, [startIdx, selectedBranches, branchesData, plByBranch]);

  const handleCopyChart = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = chartRef.current.canvas as HTMLCanvasElement;
      
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const ctx = tempCanvas.getContext('2d');
      
      if (ctx) {
        ctx.fillStyle = '#ffffff'; 
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        ctx.drawImage(canvas, 0, 0);
        
        tempCanvas.toBlob(async (blob) => {
          if (blob) {
            const item = new ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([item]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
        }, 'image/png');
      }
    } catch (err) {
      console.error('Failed to copy chart image: ', err);
      alert('Fitur salin gambar tidak didukung di browser ini atau izin ditolak.');
    }
  };

  const branchesInArea = useMemo(() => {
    let filtered = branchesData.filter((b: any) => area === 'ALL' || b.area === area);
    if (selectedBranches.length > 0) {
      filtered = filtered.filter((b: any) => selectedBranches.includes(b.code));
    }
    return filtered;
  }, [area, selectedBranches, branchesData]);

  const chartData = useMemo(() => {
    const datasets = branchesInArea
      .filter((b: any) => plByBranch[b.code] && b.openDate !== 'TBA')
      .map((branchItem: any, idx: number) => {
        const pl = plByBranch[branchItem.code];
        const branchStartIdx = getBranchStartIdx(branchItem.code, branchItem.openDate, branchesData, plByBranch);
        const cumulativeData: (number | null)[] = [];

        for (let i = effectiveStartIdx; i <= endIdx; i++) {
          if (i < branchStartIdx) {
            cumulativeData.push(null);
          } else {
            let cumulative = 0;
            for (let j = branchStartIdx; j <= i; j++) {
              cumulative += pl.netProfit[j] || 0;
            }
            cumulativeData.push(cumulative);
          }
        }

        const colors = [
          '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
          '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
          '#0ea5e9', '#d946ef', '#7c3aed', '#c084fc', '#06b6d4',
        ];
        const color = colors[idx % colors.length];

        return {
          label: branchItem.name,
          data: cumulativeData,
          borderColor: color,
          backgroundColor: color + '20',
          tension: 0.3,
          spanGaps: false,
          fill: true,
          pointRadius: 2,
          pointBackgroundColor: color,
        };
      });

    const monthLabels = MONTHS.slice(effectiveStartIdx, endIdx + 1).map((m: any) => m.label);

    return {
      labels: monthLabels,
      datasets,
    };
  }, [branchesInArea, effectiveStartIdx, endIdx, MONTHS, branchesData, plByBranch]);

  const kpis = useMemo(() => {
    let results = getAreaBranchBEPs(area, endIdx, branchesData, plByBranch);
    if (selectedBranches.length > 0) {
      results = results.filter(r => selectedBranches.includes(r.code));
    }
    const currentProfit = results.reduce((sum, r) => sum + r.monthProfit, 0);
    const cumulativeBEP = results.reduce((sum, r) => sum + r.bep, 0);
    const totalRevenue = results.reduce((sum, r) => sum + r.monthRevenue, 0);
    const totalOpex = results.reduce((sum, r) => sum + r.monthExpenses, 0);

    return { currentProfit, cumulativeBEP, totalRevenue, totalOpex };
  }, [area, selectedBranches, endIdx, branchesData, plByBranch]);

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'flex-end' }}>
        <button 
          onClick={handleCopyChart}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: '600',
            borderRadius: '6px',
            border: `1px solid ${copied ? '#10b981' : 'var(--border-light)'}`,
            backgroundColor: copied ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-secondary)',
            color: copied ? '#10b981' : 'var(--text-primary)',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          {copied ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Tersalin!
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
              Salin Chart
            </>
          )}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Laba/Rugi Bulan Ini</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: kpis.currentProfit >= 0 ? '#10b981' : '#ef4444' }}>
            {formatRupiah(kpis.currentProfit)}
          </div>
        </div>
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>BEP Kumulatif</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: kpis.cumulativeBEP >= 0 ? '#10b981' : '#ef4444' }}>
            {formatRupiah(kpis.cumulativeBEP)}
          </div>
        </div>
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Total Pendapatan</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            {formatRupiah(kpis.totalRevenue)}
          </div>
        </div>
        <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Total Pengeluaran</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            {formatRupiah(kpis.totalOpex)}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
        📊 Garis horizontal y=0 adalah titik BEP
      </div>
      <div style={{ width: '100%', overflowX: 'auto', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid var(--border-light)', marginBottom: '20px' }}>
        <div style={{ position: 'relative', height: '420px', minWidth: (endIdx - effectiveStartIdx + 1) * 120 > 1000 ? `${(endIdx - effectiveStartIdx + 1) * 120}px` : '100%', padding: '12px' }}>
          <Line
          ref={chartRef}
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: true, labels: { color: chartTheme.legendColor, font: { size: 11 } } },
              tooltip: {
                backgroundColor: chartTheme.tooltipBg,
                titleColor: chartTheme.tooltipTitle,
                bodyColor: chartTheme.tooltipBody,
                borderColor: chartTheme.tooltipBorder,
                borderWidth: 1,
              },
              datalabels: {
                anchor: 'end' as any,
                align: 'end',
                offset: 8,
                font: {
                  family: 'Inter',
                  size: 9,
                  weight: 500,
                },
                color: (context: any) => context.dataset.borderColor,
                formatter: (value: any) => {
                  if (value === 0 || value === null) return '';
                  return `Rp ${(value / 1e6).toFixed(1)} Jt`;
                },
              } as any,
            },
            scales: {
              x: {
                ticks: { color: chartTheme.tickColor, font: { size: 10 } },
                grid: { color: chartTheme.gridColorX },
              },
              y: {
                ticks: {
                  color: chartTheme.tickColor,
                  font: { size: 10 },
                  callback: (value: any) => `Rp ${(value / 1e6).toFixed(0)} Jt`,
                },
                grid: { color: chartTheme.gridColorY },
              },
            },
          }}
        />
        </div>
      </div>

      <PNLPivotTable area={area} branch="ALL" selectedBranches={selectedBranches} startIdx={effectiveStartIdx} endIdx={endIdx} />
    </div>
  );
};

export default BEPPage;
